import { ENGINEER_ROLES, isEngineerRole, ROLES, type Role } from '@veem/schema'
import { z } from 'zod'

export { ENGINEER_ROLES, isEngineerRole, ROLES, type Role }
export const actorSchema = z.object({
  id: z.string().min(1),
  role: z.enum(ROLES),
  disabledAt: z.string().nullable().optional(),
})
export type Actor = z.infer<typeof actorSchema>

export const ACTIONS = [
  'read',
  'create',
  'update',
  'delete',
  'assign',
  'transition',
  'approve',
  'pay',
] as const
export type Action = (typeof ACTIONS)[number]
export const RESOURCES = [
  'dashboard',
  'tasks',
  'directory',
  'customers',
  'orders',
  'catalog',
  'engineering',
  'production',
  'warehouse',
  'suppliers',
  'purchasing',
  'logistics',
  'invoices',
  'paymentRequests',
  'staff',
  'settings',
  'equipment',
  'scrap',
  'deals',
  'projects',
  'payment_requests',
  'users',
  'documents',
  'proposals',
  'foreign_supplies',
  'ai',
  'assistant',
  'assistantSettings',
] as const
export type Resource = (typeof RESOURCES)[number]
type Policy = Partial<Record<Resource, readonly Action[]>>
const READ: readonly Action[] = ['read']
const WRITE: readonly Action[] = ['read', 'create', 'update', 'assign', 'transition']
const ALL: readonly Action[] = ACTIONS
/** Personal tasks for oneself only; assigning to colleagues is for administrators and heads. */
const OWN_TASKS: readonly Action[] = ['read', 'create', 'update', 'transition']

const PRODUCTION_LEAD: Policy = {
  dashboard: READ,
  tasks: WRITE,
  directory: READ,
  customers: READ,
  orders: READ,
  catalog: READ,
  engineering: ['read', 'create', 'assign'],
  production: WRITE,
  warehouse: READ,
  purchasing: ['read', 'create'],
  suppliers: READ,
  equipment: WRITE,
  ai: READ,
  paymentRequests: ['read', 'create'],
  scrap: WRITE,
}

const POLICY: Record<Role, Policy> = {
  admin: {
    ...Object.fromEntries(RESOURCES.map((resource) => [resource, ALL])),
    tasks: WRITE,
    production: WRITE,
    warehouse: WRITE,
    logistics: WRITE,
    scrap: WRITE,
  },
  sales: {
    dashboard: READ,
    tasks: OWN_TASKS,
    directory: READ,
    customers: ALL,
    orders: ALL,
    catalog: READ,
    engineering: ['read', 'create', 'assign'],
    production: READ,
    warehouse: READ,
    purchasing: ['read', 'create'],
    suppliers: READ,
    invoices: READ,
    documents: WRITE,
    proposals: WRITE,
    ai: READ,
    paymentRequests: ['read', 'create'],
  },
  engineer: {
    dashboard: READ,
    tasks: OWN_TASKS,
    directory: READ,
    customers: READ,
    orders: READ,
    catalog: ALL,
    engineering: ALL,
    production: ['read', 'create', 'update'],
    warehouse: READ,
    purchasing: ['read', 'create'],
    suppliers: READ,
    documents: WRITE,
    proposals: WRITE,
    ai: READ,
    paymentRequests: ['read', 'create'],
    scrap: READ,
  },
  // «Головний конструктор»: an engineer who also hands tasks to the engineering staff.
  chief_engineer: {
    dashboard: READ,
    tasks: WRITE,
    directory: READ,
    customers: READ,
    orders: READ,
    catalog: ALL,
    engineering: ALL,
    production: ['read', 'create', 'update'],
    warehouse: READ,
    purchasing: ['read', 'create'],
    suppliers: READ,
    documents: WRITE,
    proposals: WRITE,
    ai: READ,
    paymentRequests: ['read', 'create'],
    scrap: READ,
  },
  // «Загальний»: the whole shop, the plan and every work centre.
  production_head: PRODUCTION_LEAD,
  // «По станках»: CNC work centres only (see `CNC_WORK_CENTERS`), without money or customers.
  cnc_head: {
    dashboard: READ,
    tasks: WRITE,
    directory: READ,
    catalog: READ,
    engineering: READ,
    production: WRITE,
    warehouse: READ,
    purchasing: ['read', 'create'],
    equipment: WRITE,
    ai: READ,
    scrap: WRITE,
  },
  production: PRODUCTION_LEAD,
  storekeeper: {
    dashboard: READ,
    tasks: OWN_TASKS,
    directory: READ,
    production: ['read', 'create'],
    warehouse: WRITE,
    purchasing: WRITE,
    suppliers: WRITE,
    foreign_supplies: WRITE,
    documents: READ,
    ai: READ,
    paymentRequests: ['read', 'create'],
  },
  logistician: {
    dashboard: READ,
    tasks: OWN_TASKS,
    directory: READ,
    customers: READ,
    orders: READ,
    production: READ,
    logistics: WRITE,
    foreign_supplies: READ,
    suppliers: READ,
    documents: READ,
    ai: READ,
    paymentRequests: ['read', 'create'],
  },
  accountant: {
    dashboard: READ,
    tasks: OWN_TASKS,
    directory: READ,
    customers: READ,
    orders: READ,
    invoices: ALL,
    documents: ALL,
    proposals: ALL,
    logistics: READ,
    ai: READ,
    paymentRequests: [...WRITE, 'approve', 'pay'],
    foreign_supplies: WRITE,
    suppliers: WRITE,
  },
  worker: {
    tasks: OWN_TASKS,
    directory: READ,
    production: ['read', 'transition'],
  },
}

const ALIASES: Partial<Record<Resource, Resource>> = {
  deals: 'orders',
  projects: 'catalog',
  payment_requests: 'paymentRequests',
  users: 'staff',
}

export const can = (
  actor: Actor | null | undefined,
  action: Action,
  resource: Resource = 'tasks',
): boolean => {
  if (!actor || actor.disabledAt || !Object.hasOwn(POLICY, actor.role)) return false
  if (resource === 'documents') return ['read', 'create', 'update', 'delete'].includes(action)
  if (resource === 'assistant') return ['read', 'create', 'update', 'delete'].includes(action)
  return POLICY[actor.role]?.[ALIASES[resource] ?? resource]?.includes(action) ?? false
}

export interface TaskOwnership {
  assigneeId: string
  creatorId: string
  dealId?: string | null
  status?: string
}
export const canTask = (
  actor: Actor | null | undefined,
  action: Exclude<Action, 'delete'>,
  task?: TaskOwnership,
): boolean => {
  if (!can(actor, action, 'tasks') || !actor) return false
  if (action === 'create') return true
  if (!task) return false
  if (action === 'read')
    return (
      task.creatorId === actor.id ||
      task.assigneeId === actor.id ||
      (Boolean(task.dealId) && can(actor, 'read', 'orders'))
    )
  if (actor.role !== 'admin' && task.creatorId !== actor.id && task.assigneeId !== actor.id)
    return false
  return !(
    (action === 'update' || action === 'assign') &&
    task.status !== undefined &&
    task.status !== 'open'
  )
}

const PLAN_ROLES: readonly Role[] = [
  'admin',
  'engineer',
  'chief_engineer',
  'production',
  'production_head',
]
export const canAccessProductionPlan = (role: Role) => PLAN_ROLES.includes(role)
/** Plan amounts (money) are for administrators only; others see quantities and dates. */
export const canSeeProductionPlanMoney = (role: Role) => role === 'admin'
/** Only the administrator and the chief engineer choose who draws; an engineer takes work only for themselves. */
export const canAssignEngineers = (role: Role | string) =>
  role === 'admin' || role === 'chief_engineer'
export const canConfigureProductionProgress = canAccessProductionPlan
export const canAccessProductionScrap = (role: Role) =>
  canAccessProductionPlan(role) || role === 'cnc_head'
export const canCreateInternalProductionOrder = (role: Role) =>
  [...PLAN_ROLES, 'storekeeper'].includes(role)

/**
 * Shop leads: they assign people and machines, see unassigned stages and shortages. The CNC head
 * does this only on CNC work centres.
 */
export const PRODUCTION_LEAD_ROLES: readonly Role[] = ['production', 'production_head', 'cnc_head']
export const isProductionLead = (role: Role) => PRODUCTION_LEAD_ROLES.includes(role)

/**
 * The CNC section («дільниця ЧПУ»): laser cutting, bending, milling and turning. Matched by the
 * work centre name from Settings, so a renamed centre leaves the CNC head's scope (fails closed).
 */
export const CNC_WORK_CENTERS: readonly string[] = [
  'Лазерна порізка листового металу',
  'Лазерна порізка труби',
  'Гнуття листового металу',
  'Гнуття труби',
  'Фрезерні роботи',
  'Токарні роботи',
]
export const isCncWorkCenter = (workCenter: string | null | undefined) =>
  Boolean(workCenter && CNC_WORK_CENTERS.includes(workCenter))
/** Whether this role manages stages, machines and scrap of the work centre. */
export const managesWorkCenter = (role: Role, workCenter: string | null | undefined) =>
  role === 'cnc_head' ? isCncWorkCenter(workCenter) : true
/** Laser cutting job import: the shop manager or the storekeeper confirms what was cut. */
export const canImportNesting = (actor: Actor | null | undefined) =>
  can(actor, 'assign', 'production') || can(actor, 'create', 'warehouse')
export const visibleResources = (actor: Actor) =>
  RESOURCES.filter((resource) => can(actor, 'read', resource))
